import { useEffect, useCallback, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Deployment from "../../../services/deployment";
import PubSub from "../../../services/PubSub";
import Utils from "../../../utils/utils";
import JsonHelper from "../../../utils/jsonHelper";
import { useBuildTimeConfig, useInitialBuildTimeConfig, useTemplateConfig, useTemplateMetadata, useHasPermission } from "../../../common/Hooks";
import SplitScreenEditor from "./SplitScreenEditor";
import ConfirmationPrompt from "../../controls/ConfirmationPrompt";
import UploadChanges from "./UploadChange";
import MultipleFilePickerEditor from "./MultipleFilePickerEditor";
import LanguagesEditor from "./LanguagesEditor";
import _ from 'lodash';
import { UIConfigProvider } from "./ui-config/Context";
import UIConfigEditor from "./UIConfigEditor";
import BUILD_TIME_CONFIG_TYPES from "../../../config/buildTimeConfigTypes";
import LeftNavWithButtons from "../../controls/LeftNavWithButtons";

const LivePreviewAllowedStages = ["Sandbox", "Development"];
const BuildTimeConfigManagement = () => {
    const history = useHistory();
    const { objectType, id, objectSubType } = useParams();
    const [translate] = useTranslation();
    const [onPrem, setOnPrem] = useState();
    const [enableConfigUiEditor, setEnableConfigUiEditor] = useState(false);
    const permissions = useHasPermission({
        domain: Utils.getDomain(),
        deployment: id,
        type: "deployment"
    });
    const configList = useMemo(() => {
        return [
            { _id: BUILD_TIME_CONFIG_TYPES.LANGUAGES, displayValue: translate('OTM_LANGUAGES') },
            { _id: BUILD_TIME_CONFIG_TYPES.CONFIG, displayValue: translate('OTM_UI_CONFIGURATION'), hidden: enableConfigUiEditor },
            { _id: BUILD_TIME_CONFIG_TYPES.PAGES, displayValue: translate('OTM_PAGES'), hidden: !enableConfigUiEditor },
            { _id: BUILD_TIME_CONFIG_TYPES.APPLETS, displayValue: translate('OTM_APPLETS'), hidden: !enableConfigUiEditor },
            { _id: BUILD_TIME_CONFIG_TYPES.BLOCKS, displayValue: translate('OTM_BLOCKS'), hidden: !enableConfigUiEditor },
            { _id: BUILD_TIME_CONFIG_TYPES.COMPONENTS, displayValue: translate('OTM_COMPONENTS'), hidden: !enableConfigUiEditor },
            { _id: BUILD_TIME_CONFIG_TYPES.SETTINGS, displayValue: translate('OTM_SETTINGS'), hidden: onPrem === false },
            { _id: BUILD_TIME_CONFIG_TYPES.BCS, displayValue: translate('OTM_BCS') },
            { _id: BUILD_TIME_CONFIG_TYPES.DPS, displayValue: translate('OTM_DPS') },
            { _id: BUILD_TIME_CONFIG_TYPES.SCHEMA, displayValue: translate('OTM_SCHEMA') },
            { _id: BUILD_TIME_CONFIG_TYPES.SYNCHRONIZATION, displayValue: translate('OTM_SYNCHRONIZATION') },
            { _id: BUILD_TIME_CONFIG_TYPES.AUTHORIZATION, displayValue: translate('OTM_AUTH') },
            { _id: BUILD_TIME_CONFIG_TYPES.FIELD_VALIDATION, displayValue: translate('OTM_FIELD_VALIDATION') },
            { _id: BUILD_TIME_CONFIG_TYPES.JAVASCRIPT, displayValue: translate('OTM_JAVASCRIPT') },
            { _id: BUILD_TIME_CONFIG_TYPES.STYLES, displayValue: translate('OTM_STYLES') }
        ];
    }, [onPrem, enableConfigUiEditor, translate]);
    const [retrieveData, setRetrieveData] = useState(true);
    const [selectedTab, setSelectedTab] = useState("");
    const [isLoading, setIsLoading] = useState(0);
    const [lastLivePreviewUpdate, setLastLivePreviewUpdate] = useState({});
    const [initialBuildTimeConfig, setInitialBuildTimeConfig] = useInitialBuildTimeConfig(id);
    const [buildTimeConfig, setBuildTimeConfig] = useBuildTimeConfig(id);
    const [templateConfig, setTemplateConfig] = useTemplateConfig(id);
    const [templateMetadata, setTemplateMetadata] = useTemplateMetadata(id);

    const [hasDownloadError, setHasDownloadError] = useState(false);
    const [showLivePreviewConfirmation, setShowLivePreviewConfirmation] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    const [livePreviewUrl, setLivePreviewUrl] = useState("");
    const [allowLivePreview, setAllowLivePreview] = useState(false);
    const [livePreviewWindow, setLivePreviewWindow] = useState(null);
    const [firstOpen, setFirstOpen] = useState(true);

    useEffect(() => {
        if (objectSubType && !isLoading) {
            setSelectedTab(objectSubType);
        }
    }, [objectSubType, isLoading]);

    const getBuildTimeConfig = useCallback(async (id) => {
        setHasDownloadError(false);
        setIsLoading(prev => prev + 1);
        const response = await Deployment.getBuildTimeConfig(id);
        if (!response.error) {
            setBuildTimeConfig(_.cloneDeep(response.data));
            setInitialBuildTimeConfig(_.cloneDeep(response.data));
        }
        else {
            setHasDownloadError(true);
        }
        setIsLoading(prev => prev - 1);
    }, [setBuildTimeConfig, setInitialBuildTimeConfig]);

    const getTemplateConfig = useCallback(async (id) => {
        setHasDownloadError(false);
        setIsLoading(prev => prev + 1);
        const response = await Deployment.getTemplateConfig(id);
        if (!response.error) {
            setTemplateConfig(response.data.templateData);
            setTemplateMetadata(response.data.templateMetadata);
        }
        else {
            setHasDownloadError(true);
        }
        setIsLoading(prev => prev - 1);
    }, [setTemplateConfig, setTemplateMetadata]);

    const getDeployment = useCallback(async (id) => {
        const response = await Deployment.getDeployment(id);
        if (!response.error) {
            setOnPrem(response.data.productVersion?.onPrem);
            setEnableConfigUiEditor(response.data.productVersion?.enableConfigUiEditor);
            setAllowLivePreview(LivePreviewAllowedStages.includes(response.data.environment.stage) && response.data.productVersion.livePreviewEnabled === true);
            const url = response.data.cname ? response.data.cname : response.data.otmApp.cloudfrontUrl;
            if (url) {
                setLivePreviewUrl(`https://${url}${response.data.homePage}?livePreview=true`);
            }
        }
    }, []);

    const saveLocal = useCallback(async (newConfig) => {
        setBuildTimeConfig(_.cloneDeep(newConfig));
    }, [setBuildTimeConfig]);

    const saveBuildTimeConfig = useCallback(async () => {
        setIsLoading(prev => prev + 1);
        const response = await Deployment.saveBuildTimeConfig(id, { buildTimeConfig });
        if (!response.error) {
            setInitialBuildTimeConfig(_.cloneDeep(buildTimeConfig));
        }
        setIsLoading(prev => prev - 1);
        PubSub.publish("show_info", translate("OTM_UPLOAD_FINISHED"), translate("OTM_INFO"))
    }, [buildTimeConfig, id, setInitialBuildTimeConfig, translate]);

    const computeHasChanges = useCallback(() => {
        if (!buildTimeConfig || !initialBuildTimeConfig) {
            return false;
        }
        for (const [configKey, config] of Object.entries(buildTimeConfig)) {
            if (Object.keys(config).length !== Object.keys(initialBuildTimeConfig[configKey]).length) {
                return true;
            }

            for (const [key, value] of Object.entries(config)) {
                if (value !== initialBuildTimeConfig[configKey][key]) {
                    return true;
                }
            }
        }

        return false;
    }, [buildTimeConfig, initialBuildTimeConfig]);

    const openLivePreview = useCallback(() => {
        if (!livePreviewWindow) {
            if (livePreviewUrl) {
                setLivePreviewWindow(window.open(livePreviewUrl, '_blank'));
            }
            else {
                PubSub.publish("show_info", translate("OTM_DEPLOYMENT_NO_URL"));
            }
            setFirstOpen(true);
        }
        else {
            setShowLivePreviewConfirmation(true);
        }
    }, [livePreviewWindow, livePreviewUrl, translate]);

    const sendLivePreviewUpdate = useCallback(async ({ livePreviewConfig }) => {
        if (livePreviewWindow) {
            const overridesConfig = livePreviewConfig || buildTimeConfig;

            setIsLoading(prev => prev + 1);
            const languages = {};
            Object.keys(overridesConfig.languages).forEach(lang => {
                var fileName = lang.replace('/l10n/', '').replace('.json', '');
                languages[fileName] = overridesConfig.languages[lang];
            });

            var config = mergeConfigByKey({ key: 'config', templateConfig, buildTimeConfig: overridesConfig });
            var bcs = mergeConfigByKey({ key: 'bcs', templateConfig, buildTimeConfig: overridesConfig });
            var dps = mergeConfigByKey({ key: 'dps', templateConfig, buildTimeConfig: overridesConfig });
            var schema = mergeConfigByKey({ key: 'schema', templateConfig, buildTimeConfig: overridesConfig });
            var sync = mergeConfigByKey({ key: 'sync', templateConfig, buildTimeConfig: overridesConfig });
            var auth = mergeConfigByKey({ key: 'auth', templateConfig, buildTimeConfig: overridesConfig });
            var val = mergeConfigByKey({ key: 'val', templateConfig, buildTimeConfig: overridesConfig });

            var js = null;
            var css = null;

            if (firstOpen || !_.isEqual(overridesConfig.js, lastLivePreviewUpdate.js)) {
                var jsResponse = await Deployment.compileJs({ deploymentId: id, jsData: overridesConfig.js });
                if (!jsResponse.error) {
                    js = jsResponse.data;
                }
            }
            if (firstOpen || !_.isEqual(overridesConfig.scss, lastLivePreviewUpdate.scss)) {
                const overridesScss = {};
                Object.keys(overridesConfig.scss).forEach(file => {
                    overridesScss[`/overrides${file}`] = overridesConfig.scss[file];
                });
                var response = await Deployment.compileScss({ deploymentId: id, scssData: { ...overridesScss, ...templateConfig.scss } });
                if (!response.error) {
                    css = response.data;
                }
            }

            setLastLivePreviewUpdate({ js: _.cloneDeep(overridesConfig.js), scss: _.cloneDeep(overridesConfig.scss) });

            const sendData = {
                source: "livePreview",
                languages,
                config,
                bcs,
                dps,
                schema,
                sync,
                auth,
                val,
                js,
                css
            };

            livePreviewWindow.postMessage(sendData, "*");
            setIsLoading(prev => prev - 1);
        }
    }, [templateConfig, buildTimeConfig, livePreviewWindow, lastLivePreviewUpdate, firstOpen, id]);

    const canSendUpdate = useCallback(() => {
        return livePreviewWindow !== null && isLoading === 0;
    }, [livePreviewWindow, isLoading]);

    const mergeConfigByKey = ({ key, templateConfig, buildTimeConfig }) => {
        var config = {};
        Object.keys(templateConfig[key]).forEach(file => {
            config = JSON.parse(templateConfig[key][file]);
        });
        Object.keys(buildTimeConfig[key]).forEach(file => {
            config = JsonHelper.deepMerge(config, JSON.parse(buildTimeConfig[key][file]));
        });

        return config;
    }

    const jsonValidator = (data) => {
        return JsonHelper.isJsonString(data);
    }

    const getDisplayMessage = useMemo(() => {
        if (hasDownloadError) {
            return translate('OTM_BUILD_TIME_CONFIG_ERROR');
        }
        if (isLoading > 0) {
            return <>
                <div className="spinner-border spinner-border-sm text-secondary" />
                <label className="ps-2">{translate("OTM_LOADING_BUILD_TIME_CONFIG")}</label>
            </>;
        }

        return translate("OTM_BUILD_TIME_CONFIG_INFO");
    }, [hasDownloadError, isLoading, translate]);

    const renderRightView = useMemo(() => {
        switch (selectedTab.toLowerCase()) {
            case BUILD_TIME_CONFIG_TYPES.LANGUAGES: {
                return <LanguagesEditor
                    initialBuildTimeConfig={initialBuildTimeConfig}
                    buildTimeConfig={buildTimeConfig}
                    templateConfig={templateConfig}
                    onSave={saveLocal}
                    onLivePreviewUpdate={sendLivePreviewUpdate}
                    canSendUpdate={canSendUpdate()}
                />
            }
            case BUILD_TIME_CONFIG_TYPES.CONFIG: {
                return <SplitScreenEditor
                    heading={translate("OTM_PAGE_CONFIG_HEADER")}
                    mode="javascript"
                    configKey="config"
                    basePath="/config"
                    emptyFile="{}"
                    validator={jsonValidator}
                    baseDataFilePath="/config.json"
                    initialBuildTimeConfig={initialBuildTimeConfig}
                    buildTimeConfig={buildTimeConfig}
                    templateConfig={templateConfig}
                    onSave={saveLocal}
                    onLivePreviewUpdate={sendLivePreviewUpdate}
                    canSendUpdate={canSendUpdate()} />
            }
            case BUILD_TIME_CONFIG_TYPES.PAGES: {
                return (
                    <UIConfigProvider buildTimeConfig={buildTimeConfig} templateConfig={templateConfig} templateMetadata={templateMetadata} onSave={saveLocal} onLivePreviewUpdate={sendLivePreviewUpdate} canSendUpdate={canSendUpdate()}>
                        <UIConfigEditor />
                    </UIConfigProvider>
                );
            }
            case BUILD_TIME_CONFIG_TYPES.APPLETS: {
                return (
                    <UIConfigProvider buildTimeConfig={buildTimeConfig} templateConfig={templateConfig} templateMetadata={templateMetadata} onSave={saveLocal} onLivePreviewUpdate={sendLivePreviewUpdate} canSendUpdate={canSendUpdate()}>
                        <UIConfigEditor />
                    </UIConfigProvider>
                );
            }
            case BUILD_TIME_CONFIG_TYPES.BLOCKS: {
                return (
                    <UIConfigProvider buildTimeConfig={buildTimeConfig} templateConfig={templateConfig} templateMetadata={templateMetadata} onSave={saveLocal} onLivePreviewUpdate={sendLivePreviewUpdate} canSendUpdate={canSendUpdate()}>
                        <UIConfigEditor />
                    </UIConfigProvider>
                );
            }
            case BUILD_TIME_CONFIG_TYPES.COMPONENTS: {
                return (
                    <UIConfigProvider buildTimeConfig={buildTimeConfig} templateConfig={templateConfig} templateMetadata={templateMetadata} onSave={saveLocal} onLivePreviewUpdate={sendLivePreviewUpdate} canSendUpdate={canSendUpdate()}>
                        <UIConfigEditor />
                    </UIConfigProvider>
                );
            }
            case BUILD_TIME_CONFIG_TYPES.SETTINGS: {
                return <SplitScreenEditor
                    heading={translate("OTM_SETTINGS_HEADER")}
                    mode="javascript"
                    configKey="settings"
                    basePath="/init"
                    emptyFile="{}"
                    validator={jsonValidator}
                    baseDataFilePath="/init/settings.json"
                    initialBuildTimeConfig={initialBuildTimeConfig}
                    buildTimeConfig={buildTimeConfig}
                    templateConfig={templateConfig}
                    onSave={saveLocal}
                    onLivePreviewUpdate={sendLivePreviewUpdate}
                    canSendUpdate={canSendUpdate()} />
            }
            case BUILD_TIME_CONFIG_TYPES.BCS: {
                return <SplitScreenEditor
                    heading={translate("OTM_BUSINESS_COMPONENTS_HEADER")}
                    mode="javascript"
                    configKey="bcs"
                    basePath="/init"
                    emptyFile="{}"
                    validator={jsonValidator}
                    baseDataFilePath="/init/bc.json"
                    initialBuildTimeConfig={initialBuildTimeConfig}
                    buildTimeConfig={buildTimeConfig}
                    templateConfig={templateConfig}
                    onSave={saveLocal}
                    onLivePreviewUpdate={sendLivePreviewUpdate}
                    canSendUpdate={canSendUpdate()} />
            }
            case BUILD_TIME_CONFIG_TYPES.DPS: {
                return <SplitScreenEditor
                    heading={translate("OTM_DATA_PROVIDERS_HEADER")}
                    mode="javascript"
                    configKey="dps"
                    basePath="/init"
                    emptyFile="{}"
                    validator={jsonValidator}
                    baseDataFilePath="/init/dp.json"
                    initialBuildTimeConfig={initialBuildTimeConfig}
                    buildTimeConfig={buildTimeConfig}
                    templateConfig={templateConfig}
                    onSave={saveLocal}
                    onLivePreviewUpdate={sendLivePreviewUpdate}
                    canSendUpdate={canSendUpdate()} />
            }
            case BUILD_TIME_CONFIG_TYPES.SCHEMA: {
                return <SplitScreenEditor
                    heading={translate("OTM_SCHEMA_HEADER")}
                    mode="javascript"
                    configKey="schema"
                    basePath="/init"
                    emptyFile="{}"
                    validator={jsonValidator}
                    baseDataFilePath="/init/schema.json"
                    initialBuildTimeConfig={initialBuildTimeConfig}
                    buildTimeConfig={buildTimeConfig}
                    templateConfig={templateConfig}
                    onSave={saveLocal}
                    onLivePreviewUpdate={sendLivePreviewUpdate}
                    canSendUpdate={canSendUpdate()} />
            }
            case BUILD_TIME_CONFIG_TYPES.SYNCHRONIZATION: {
                return <SplitScreenEditor
                    heading={translate("OTM_SYNC_HEADER")}
                    mode="javascript"
                    configKey="sync"
                    basePath="/init"
                    emptyFile="{}"
                    validator={jsonValidator}
                    baseDataFilePath="/init/sync.json"
                    initialBuildTimeConfig={initialBuildTimeConfig}
                    buildTimeConfig={buildTimeConfig}
                    templateConfig={templateConfig}
                    onSave={saveLocal}
                    onLivePreviewUpdate={sendLivePreviewUpdate}
                    canSendUpdate={canSendUpdate()} />
            }
            case BUILD_TIME_CONFIG_TYPES.AUTHORIZATION: {
                return <SplitScreenEditor
                    heading={translate("OTM_AUTH_HEADER")}
                    mode="javascript"
                    configKey="auth"
                    basePath="/init"
                    emptyFile="{}"
                    validator={jsonValidator}
                    baseDataFilePath="/init/auth.json"
                    initialBuildTimeConfig={initialBuildTimeConfig}
                    buildTimeConfig={buildTimeConfig}
                    templateConfig={templateConfig}
                    onSave={saveLocal}
                    onLivePreviewUpdate={sendLivePreviewUpdate}
                    canSendUpdate={canSendUpdate()} />
            }
            case BUILD_TIME_CONFIG_TYPES.FIELD_VALIDATION: {
                return <SplitScreenEditor
                    heading={translate("OTM_FIELD_VALIDATION_HEADER")}
                    mode="javascript"
                    configKey="val"
                    basePath="/init"
                    emptyFile="{}"
                    validator={jsonValidator}
                    baseDataFilePath="/init/val.json"
                    initialBuildTimeConfig={initialBuildTimeConfig}
                    buildTimeConfig={buildTimeConfig}
                    templateConfig={templateConfig}
                    onSave={saveLocal}
                    onLivePreviewUpdate={sendLivePreviewUpdate}
                    canSendUpdate={canSendUpdate()} />
            }
            case BUILD_TIME_CONFIG_TYPES.JAVASCRIPT: {
                return <MultipleFilePickerEditor
                    heading={translate("OTM_JAVASCRIPT_HEADER")}
                    mode="javascript"
                    configKey="js"
                    basePath="/js"
                    baseTemplatePath="/js"
                    emptyFile=""
                    initialBuildTimeConfig={initialBuildTimeConfig}
                    buildTimeConfig={buildTimeConfig}
                    templateConfig={templateConfig}
                    onSave={saveLocal}
                    onLivePreviewUpdate={sendLivePreviewUpdate}
                    canSendUpdate={canSendUpdate()}
                    fixedFiles={['/js/index.js']} />
            }
            case BUILD_TIME_CONFIG_TYPES.STYLES: {
                return <MultipleFilePickerEditor
                    heading={translate("OTM_STYLES_HEADER")}
                    mode="sass"
                    configKey="scss"
                    basePath="/styles"
                    emptyFile=""
                    initialBuildTimeConfig={initialBuildTimeConfig}
                    buildTimeConfig={buildTimeConfig}
                    templateConfig={templateConfig}
                    onSave={saveLocal}
                    onLivePreviewUpdate={sendLivePreviewUpdate}
                    canSendUpdate={canSendUpdate()}
                    allowFolderCreation={false}
                    fixedFiles={['/styles/custom.scss', '/styles/themes.scss', '/styles/variables.scss']} />
            }
            case "upload": {
                return <UploadChanges
                    hasChanges={hasChanges}
                    disableButtons={isLoading > 0}
                    onRevert={() => saveLocal(initialBuildTimeConfig)}
                    onUpload={() => saveBuildTimeConfig()} />
            }
            default: {
                return <div className="ps-2">
                    <label className="h6">{getDisplayMessage}</label>
                </div>
            }
        }
    }, [selectedTab, initialBuildTimeConfig, buildTimeConfig, templateConfig, templateMetadata, hasChanges, getDisplayMessage, isLoading, canSendUpdate,
        sendLivePreviewUpdate, translate, saveBuildTimeConfig, saveLocal]);

    const renderButtons = useMemo(() => {
        return <>
            <Button
                onClick={() => setSelectedTab('Upload')}
                className="text-white btn-sm mt-2 w-100 btn-secondary"
                disabled={!permissions.update || hasDownloadError || isLoading > 0}
            > {translate("OTM_UPLOAD")} </Button>
            <Button
                onClick={openLivePreview}
                className="text-white btn-sm mt-2 w-100 btn-secondary"
                disabled={!permissions.update || hasDownloadError || isLoading > 0 || !allowLivePreview}
            > {translate("OTM_LIVE_PREVIEW")} </Button>
        </>
    }, [permissions.update, hasDownloadError, isLoading, allowLivePreview, openLivePreview, translate]);

    useEffect(() => {
        if (livePreviewWindow && firstOpen) {
            setTimeout(() => sendLivePreviewUpdate({}), 2000);
            setFirstOpen(false);
        }
    }, [sendLivePreviewUpdate, livePreviewWindow, firstOpen]);

    useEffect(() => {
        const newHasChanges = computeHasChanges();
        setHasChanges(newHasChanges);
    }, [computeHasChanges]);

    useEffect(() => {
        if (id && retrieveData) {
            getDeployment(id);
            if (!buildTimeConfig) {
                getBuildTimeConfig(id);
            }

            if (!templateConfig) {
                getTemplateConfig(id);
            }

            setRetrieveData(false);
        }
    }, [id, buildTimeConfig, templateConfig, retrieveData, getDeployment, getBuildTimeConfig, getTemplateConfig])

    if (!permissions.read) {
        return (
            <div className="p-4">
                {translate("OTM_NO_PERMISSION_DEPLOYMENT")}
            </div>
        );
    }

    return <>
        <ConfirmationPrompt
            show={showConfirmation}
            onConfirm={() => { setShowConfirmation(false); saveBuildTimeConfig(); }}
            onCancel={() => setShowConfirmation(false)} />
        <ConfirmationPrompt
            show={showLivePreviewConfirmation}
            message={"OTM_LIVE_PREVIEW_CONFIRMATION"}
            onConfirm={() => {
                setShowLivePreviewConfirmation(false);
                setLivePreviewWindow(window.open(livePreviewUrl, '_blank'));
                setFirstOpen(true);
            }}
            onCancel={() => setShowLivePreviewConfirmation(false)} />
        <div className="row h-100">
            <div className="col-2 col-xl-1 border-end p-0 px-3 d-block">
                <LeftNavWithButtons
                    listData={configList}
                    listAttr={"displayValue"}
                    listOnClick={(_id) => { history.push(`/${objectType}/${id}/${_id}/`); setSelectedTab(_id); }}
                    selectedTab={selectedTab}
                    disabled={hasDownloadError || isLoading !== 0}
                    buttons={renderButtons}
                />
            </div>
            <div className="col-10 col-xl-11 mt-3 h-100">
                {renderRightView}
            </div>
        </div>
    </>
}

export default BuildTimeConfigManagement;